import { createContext, useCallback, useContext, useState } from 'react';

export interface AccordionItemData {
  uid: string;
  heading: React.ReactNode;
  content: React.ReactNode;
  icon?: React.ReactNode;
}

const AccordionContext = createContext<AccordionContextProps>({
  openPanels: [],
  panels: [],
  togglePanel: () => {
    /** Placeholder for real function */
  },
  /** Placeholder for real function */
  scrollToOpenPanel: false,
  panelIsOpen: () => false,
  expandAll: () => {
    /** Placeholder for real function */
  },
  collapseAll: () => {
    /** Placeholder for real function */
  },
  itemClassName: undefined,
  contentClassName: undefined,
});

export function useAccordionContext() {
  return useContext(AccordionContext);
}

export function useAccordionItemContext(uid: string) {
  const { panelIsOpen, togglePanel, scrollToOpenPanel } = useAccordionContext();

  // Whether the panel should be open
  const isOpen = panelIsOpen(uid ?? '');

  const toggleOpen = useCallback((open?: boolean) => togglePanel(uid, open), [togglePanel, uid]);

  return {
    isOpen,
    toggleOpen,
    scrollToOpenPanel,
  };
}

export function AccordionContextProvider({
  children,
  panels,
  singleOpenPanel,
  scrollToOpenPanel,
  defaultOpenPanels = [],
  itemClassName,
  contentClassName,
}: AccordionContextProviderProps) {
  const [openPanelIds, setOpenPanelIds] = useState<string[]>(
    defaultOpenPanels.map((x) => x.uid ?? '')
  );

  const togglePanel = useCallback(
    (uid: string, open?: boolean) => {
      setOpenPanelIds((openIds) => {
        const currentlyOpen = openIds.includes(uid);
        const shouldOpen = open ?? !currentlyOpen;

        if (shouldOpen && !currentlyOpen) {
          return singleOpenPanel ? [uid] : [...openIds, uid];
        } else if (!shouldOpen && currentlyOpen) {
          return singleOpenPanel ? [] : openIds.filter((x) => x != uid);
        }
        // No change, return the original array, don't create a new one.
        return openIds;
      });
    },
    [singleOpenPanel]
  );

  const panelIsOpen = useCallback((uid: string) => openPanelIds.includes(uid), [openPanelIds]);

  const expandAll = useCallback(() => {
    const allPanelIds = panels.map((panel) => panel.uid ?? '').filter(Boolean);
    // Open items in reverse order with delay to prevent scroll conflicts
    allPanelIds.reverse().forEach((id: string, index: number) => {
      setTimeout(() => {
        togglePanel(id, true);
      }, index * 100);
    });
  }, [panels, togglePanel]);

  const collapseAll = useCallback(() => {
    const allPanelIds = panels.map((panel) => panel.uid ?? '').filter(Boolean);
    // Close items in reverse order with delay to prevent scroll conflicts
    allPanelIds.reverse().forEach((id: string, index: number) => {
      setTimeout(() => {
        togglePanel(id, false);
      }, index * 100);
    });
  }, [panels, togglePanel]);

  return (
    <AccordionContext.Provider
      value={{
        openPanels: openPanelIds
          .map((uid) => panels.find((p) => p.uid === uid))
          .filter((x) => x !== undefined) as AccordionItemData[],
        panels,
        togglePanel,
        panelIsOpen,
        scrollToOpenPanel: !!scrollToOpenPanel,
        expandAll,
        collapseAll,
        itemClassName,
        contentClassName,
      }}
    >
      {children}
    </AccordionContext.Provider>
  );
}

interface AccordionContextProviderProps extends React.PropsWithChildren {
  panels: AccordionItemData[];
  singleOpenPanel: boolean;
  scrollToOpenPanel?: boolean;
  defaultOpenPanels: AccordionItemData[];
  itemClassName?: string;
  contentClassName?: string;
}

export interface AccordionContextProps {
  openPanels: AccordionItemData[];
  panels: AccordionItemData[];
  scrollToOpenPanel: boolean;
  togglePanel: (uid: string, open?: boolean) => void;
  panelIsOpen: (uid: string) => boolean;
  expandAll: () => void;
  collapseAll: () => void;
  itemClassName?: string;
  contentClassName?: string;
}
